import React from 'react';
import { useAuth } from '../context/AuthContext';
import { CheckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const Pricing = () => {
    const { user } = useAuth();

    const handleSubscribe = (plan) => {
        toast.success(`Subscribed to ${plan} plan! (Mock)`);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
                <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Pricing Plans</h2>
                <p className="mt-4 text-xl text-gray-600">Choose the right plan for your business</p>
            </div>

            <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0 xl:grid-cols-3">
                {/* Basic Plan */}
                <div className="border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200 bg-white">
                    <div className="p-6">
                        <h2 className="text-lg leading-6 font-medium text-gray-900">Basic</h2>
                        <p className="mt-4 text-sm text-gray-500">Essential features for small hotels.</p>
                        <p className="mt-8">
                            <span className="text-4xl font-extrabold text-gray-900">$29</span>
                            <span className="text-base font-medium text-gray-500">/mo</span>
                        </p>
                        <button
                            onClick={() => handleSubscribe('Basic')}
                            className="mt-8 block w-full bg-blue-600 border border-transparent rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-blue-700"
                        >
                            Buy Basic
                        </button>
                    </div>
                    <div className="pt-6 pb-8 px-6">
                        <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">What's included</h3>
                        <ul className="mt-6 space-y-4">
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">Up to 100 inventory items</span>
                            </li>
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">Basic sales tracking</span>
                            </li>
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">5 AI Analysis Credits/mo</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Pro Plan */}
                <div className="border border-blue-500 rounded-lg shadow-md divide-y divide-gray-200 bg-white relative">
                    <div className="absolute top-0 right-0 -mr-1 -mt-1 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold px-3 py-1 bg-blue-500 rounded-full absolute -top-3 right-4 transform translate-x-1/2">Popular</span>
                    </div>
                    <div className="p-6">
                        <h2 className="text-lg leading-6 font-medium text-gray-900">Pro</h2>
                        <p className="mt-4 text-sm text-gray-500">Advanced features for growing businesses.</p>
                        <p className="mt-8">
                            <span className="text-4xl font-extrabold text-gray-900">$79</span>
                            <span className="text-base font-medium text-gray-500">/mo</span>
                        </p>
                        <button
                            onClick={() => handleSubscribe('Pro')}
                            className="mt-8 block w-full bg-blue-600 border border-transparent rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-blue-700"
                        >
                            Buy Pro
                        </button>
                    </div>
                    <div className="pt-6 pb-8 px-6">
                        <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">What's included</h3>
                        <ul className="mt-6 space-y-4">
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">Unlimited inventory items</span>
                            </li>
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">Advanced sales analytics</span>
                            </li>
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">50 AI Analysis Credits/mo</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Enterprise Plan */}
                <div className="border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200 bg-white">
                    <div className="p-6">
                        <h2 className="text-lg leading-6 font-medium text-gray-900">Enterprise</h2>
                        <p className="mt-4 text-sm text-gray-500">Custom solutions for large hotels.</p>
                        <p className="mt-8">
                            <span className="text-4xl font-extrabold text-gray-900">$199</span>
                            <span className="text-base font-medium text-gray-500">/mo</span>
                        </p>
                        <button
                            onClick={() => handleSubscribe('Enterprise')}
                            className="mt-8 block w-full bg-gray-800 border border-transparent rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-gray-900"
                        >
                            Contact Sales
                        </button>
                    </div>
                    <div className="pt-6 pb-8 px-6">
                        <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">What's included</h3>
                        <ul className="mt-6 space-y-4">
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">Everything in Pro</span>
                            </li>
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">Custom Integrations</span>
                            </li>
                            <li className="flex space-x-3">
                                <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                                <span className="text-sm text-gray-500">Unlimited AI Analysis Credits</span>
                            </li>
                        </ul>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Pricing;
